import type { TFunction } from 'i18next';

import { PASSWORD_MIN_LENGTH } from 'shared/forms';

import { RESET_PASSWORD_FIELDS, RESET_PASSWORD_VALIDATION_ERROR_CODES } from '../consts';

type ResetPasswordValidationErrorCode =
  (typeof RESET_PASSWORD_VALIDATION_ERROR_CODES)[keyof typeof RESET_PASSWORD_VALIDATION_ERROR_CODES];

type ResetPasswordFormField = (typeof RESET_PASSWORD_FIELDS)[keyof typeof RESET_PASSWORD_FIELDS];

/**
 * Resolves a localized validation error message for a reset-password server
 * validation error code.
 *
 * - New-password field: SAME_AS_CURRENT_PASSWORD maps to the dedicated must-differ key.
 * - Code field: REQUIRED maps to a required message; format errors
 *   (INVALID_LENGTH, INVALID_CHARACTERS) map to the invalid-code key since
 *   any format failure means the code is wrong.
 * - Password-confirmation field: REQUIRED maps to a required message; format
 *   errors (INVALID_LENGTH, INVALID_CHARACTERS) map to the passwords-do-not-match
 *   key, preserving the field's responsibility of showing a mismatch message.
 * - New-password field (default): standard form-validation messages.
 *
 * @param errorCode - Server validation error code from the reset-password API.
 * @param field - Form field the error belongs to. Defaults to the new-password field.
 */
export const getResetPasswordValidationErrorMessage = (
  errorCode: ResetPasswordValidationErrorCode,
  t: TFunction,
  field: ResetPasswordFormField = RESET_PASSWORD_FIELDS.NEW_PASSWORD
) => {
  if (errorCode === RESET_PASSWORD_VALIDATION_ERROR_CODES.PASSWORDS_DO_NOT_MATCH) {
    return t('forms.validation.passwordsDoNotMatch');
  }

  if (errorCode === RESET_PASSWORD_VALIDATION_ERROR_CODES.INVALID_CODE) {
    return t('auth.resetPassword.errors.invalidCode');
  }

  if (errorCode === RESET_PASSWORD_VALIDATION_ERROR_CODES.SAME_AS_CURRENT_PASSWORD) {
    return t('forms.validation.sameAsCurrentPassword');
  }

  if (field === RESET_PASSWORD_FIELDS.CODE) {
    if (errorCode === RESET_PASSWORD_VALIDATION_ERROR_CODES.REQUIRED) {
      return t('forms.validation.required', { field: t('auth.resetPassword.fields.code') });
    }

    return t('auth.resetPassword.errors.invalidCode');
  }

  if (field === RESET_PASSWORD_FIELDS.PASSWORD_CONFIRMATION) {
    if (errorCode === RESET_PASSWORD_VALIDATION_ERROR_CODES.REQUIRED) {
      return t('forms.validation.required', { field: t('auth.resetPassword.fields.passwordConfirmation') });
    }

    return t('forms.validation.passwordsDoNotMatch');
  }

  switch (errorCode) {
    case RESET_PASSWORD_VALIDATION_ERROR_CODES.REQUIRED:
      return t('forms.validation.required', { field: t('forms.fields.password') });
    case RESET_PASSWORD_VALIDATION_ERROR_CODES.INVALID_LENGTH:
      return t('forms.validation.minLength', { field: t('forms.fields.password'), min: PASSWORD_MIN_LENGTH });
    case RESET_PASSWORD_VALIDATION_ERROR_CODES.INVALID_CHARACTERS:
      return t('forms.validation.repeatedCharacters', { field: t('forms.fields.password') });
    default:
      return t('errors.somethingWentWrongTitle');
  }
};
